import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape AUD-01 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { AuditRecord } from "@tdelivr/api-client/generated/security/audit.js";

import {
  actorOf,
  auditPage,
  boundedNote,
  clockTime,
  crossTenantActor,
  ingestionLagSeconds,
  pageBoundaryHazard,
  stateOf,
  statesPresent,
  PER_FETCH,
  type AuditOrder,
} from "../tenant-audit.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The tenant audit log: everything this tenant's staff and the platform's did in their account.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `audit`. Every measurement is the one that file
 * computes for its own template, and that template is **byte-identical** to the one the dispatch
 * console's offer monitor was measured against — the two work-package files share `hdrUI()`,
 * `gridUI()`, `badge()`, `glyph()` and the whole page body, which was checked rather than assumed.
 * So the page body is a 16px-gap grid padded `20px 22px 28px`; the data grid is a 16px-radius card
 * holding a `12px 14px` toolbar, a `10px 16px` header strip on `--sunken`, `12px 16px` rows and a
 * `10px 14px` pager; the tracks are the design's own `minmax(0, 1.5fr)` then one `minmax(0, 1fr)`
 * per remaining column then `190px`, 14px apart; and the closing note is a 14px-radius
 * `--accentTint` callout — the design's `lock` kind — with 20px of glyph and 13.5/20 text.
 *
 * **All three of the design's columns have a source, and so does the one thing that makes this page
 * more than a log viewer.** `When` is `occurredAt`; `Action` is `action`; `Actor` is the innermost
 * link of `actorChain`, which is who *did* it rather than `producerSubject`, who reported it; and
 * the design's "platform staff actions are marked" is `actorChain[].kind === "impersonator"`.
 *
 * **What is absent is the pager's second page, and that is the finding.** AUD-01 orders by
 * `(occurred_at DESC, event_id DESC)` and publishes one cursor: `before`, a bare instant, applied as
 * `occurred_at < $before`. Following it with the last row's `occurredAt` skips every record sharing
 * that instant — and on a log whose own closing note promises that platform-staff actions "always
 * appear here", a page that quietly dropped one would make that sentence untrue while looking
 * complete. So this board takes AUD-01's maximum of 200 in one request, says so, and does not page.
 * `pageBoundaryHazard()` in `tenant-audit.ts` holds the rule with the evidence beside it, and R-12
 * in `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 */

const auditApi = services.security__audit;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: AuditOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface TenantAuditProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly rows: readonly AuditRecord[];
}

export function TenantAudit({ transport, crumbs }: TenantAuditProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<AuditOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = auditApi.createAuditClient(transport);
    try {
      // `limit` only. The cursor is deliberately not sent: see the header, and R-12.
      const answered = await client.listAuditRecords(String(PER_FETCH), undefined);
      setLoaded({ rows: answered.auditRecords });
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. An audit log showing some of its
      // rows with no explanation is worse than one showing none.
      setFailure(stateForError(error, ["audit:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an administrator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = useMemo(
    () => auditPage(loaded?.rows ?? [], { search, order, page, perPage }, state),
    [loaded, search, order, page, perPage, state],
  );

  /*
   * No `observedAt`, so no stale state.
   *
   * AUD-01's listing publishes no page-level instant. Each row carries `ingestedAt`, which is when
   * that record arrived rather than when this page was answered, so it cannot stand in for a
   * watermark: a quiet tenant's newest record is hours old and the board is not hours stale.
   * Recorded in R-12 rather than faked from the moment the browser asked.
   */
  const viewState = failure ?? stateForList(loaded?.rows);

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded === undefined ? undefined : pageBoundaryHazard(loaded.rows),
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>When</span>
      <span>Actor</span>
      <span>Action</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Tenant audit"
        source="AUD-01"
        description="Everything your staff and ours did in your account. Platform staff actions are marked."
        action={
          <button
            type="button"
            className="records__primary"
            /*
             * Drawn, and refused — the same treatment R-08 gave the demand queue's `+ Manual
             * assign`, R-09 the live map's card buttons and R-10 the offer monitor's `+ Export
             * offers`.
             *
             * The design's form is "Export audit log" over a From/To range, an actor filter and a
             * format, with the promise that "exports are themselves recorded in the audit log".
             * AUD-01's listing takes `limit` and `before` and nothing else: there is no `from`, no
             * actor filter, and no export operation anywhere on the platform (R-01). The recording
             * half would need `audit:write`, which is the permission to write the log this page
             * reads, and this page does not ask for it. The toolbar's own Export below writes what
             * this board holds, which is a different and smaller promise — and one nothing records.
             */
            disabled
            title="AUD-01's listing takes no date range and no actor filter, and no operation exports a log. See R-12."
          >
            <span aria-hidden="true" className="records__primary-glyph">
              add
            </span>
            Export audit log
          </button>
        }
      />

      {/*
        No tiles, where the design has four.

        `Events today · 8,412`, `Platform staff actions · 2`, `Denied · 5` and `Exports · 0 this
        week` are each a count over a window, and neither half is published: no listing on this
        platform publishes a total (R-04, measured at 0 of 70 non-internal collection reads), and
        AUD-01's only filter is `before`, so "today" and "this week" cannot be asked for. A count of
        the 200 rows this page happens to hold under a label that means "today" would be a number an
        administrator would work from. R-12.
      */}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              id="tenant-audit-search"
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            id="tenant-audit-state"
            aria-label="Filter by state"
            value={state ?? ""}
            onChange={(event) => {
              setState(event.target.value === "" ? undefined : event.target.value);
              setPage(1);
            }}
          >
            {/*
              The design's word for the unfiltered option, over the states actually present in what
              came back — which is what the design's own `gridUI` does, and here it is also the only
              thing that can be done: AUD-01's listing takes no state filter, so there is no request
              that would offer the other states. The line under the pager says so.
            */}
            <option value="">All states</option>
            {statesPresent(loaded?.rows ?? []).map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
          <select
            id="tenant-audit-order"
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as AuditOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, choice.format);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose="Records appear here as soon as a service reports something your staff or ours did."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            columns: 4,
            rowClassName: "records__row",
            // Both strings come from the trail the shell already draws, so the
            // sentence can never disagree with the breadcrumb above it.
            page: crumbs?.at(-1) ?? "this page",
            tenant: crumbs?.[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((record) => {
              const shown = stateOf(record);
              const lag = ingestionLagSeconds(record);
              return (
                <div
                  key={record.eventId}
                  className={
                    crossTenantActor(record) ? "records__row records__row--cross" : "records__row"
                  }
                >
                  <span
                    className="records__when"
                    /*
                     * The gap between when it happened and when the log was told, where there is
                     * one. Not a column — a column the design does not draw is design content
                     * invented here — but it is the answer to "why is this row sorted there": the
                     * board is ordered on `occurredAt`, which whoever reported the fact supplied.
                     */
                    {...(lag === undefined || lag < 1
                      ? {}
                      : {
                          title: `Recorded by the audit log ${lag} s later, at ${record.ingestedAt}.`,
                        })}
                  >
                    {clockTime(record)}
                  </span>
                  <span className="records__actor">{actorOf(record)}</span>
                  <span className="records__action">{record.action}</span>
                  <span className="records__state">
                    <StateBadge
                      code="AUD-01"
                      status={record.outcome}
                      classKey={shown.classKey}
                      label={shown.label}
                      {...(shown.label === "cross-tenant"
                        ? {
                            title: `An actor chain on this record includes an impersonator, which is how platform staff acting on your account are recorded. The outcome itself was ${record.outcome}.`,
                          }
                        : {})}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
            </span>
            <span className="records__pager-controls">
              <select
                id="tenant-audit-per-page"
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {/*
          What this board actually fetched, said rather than hidden — the line the dispatch console's
          two boards draw for the reason R-08 defends. The pager above it pages the 200 rows the
          browser holds; this says that those 200 are the log's newest and that nothing below them is
          reachable safely.
        */}
        {bounded === undefined ? null : (
          <p className="records__bounded" role="status">
            {bounded}
          </p>
        )}
      </div>

      <div className="records__note">
        <span aria-hidden="true" className="records__note-glyph">
          lock
        </span>
        <span>Append-only. Platform staff actions on your account always appear here.</span>
      </div>
    </section>
  );
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * enum. Excel takes an HTML table, which is what `application/vnd.ms-excel` has always meant in
 * practice, and PDF is the browser's own print dialogue.
 *
 * **This export is not recorded anywhere**, where the design's own form promises that "exports are
 * themselves recorded in the audit log". Recording it would need `audit:write` — the permission to
 * append to the log this page reads — which this page deliberately does not ask for, and there is no
 * narrower operation. R-12.
 */
function exportRows(rows: readonly AuditRecord[], format: ExportFormat): void {
  const header = ["When", "Actor", "Action", "State"];
  const body = rows.map((record) => [
    // The full instant rather than the board's clock time: a row read out of a file months later
    // has no "today" to be read against.
    record.occurredAt,
    actorOf(record),
    record.action,
    stateOf(record).label,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "tenant-audit.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "tenant-audit.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>tenant-audit</title></head><body><h2 style="font-family:sans-serif">tenant-audit</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
